import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import { useEffect, useId, useRef, useState } from 'react';
import { Headline, Svg } from '../../snippets';
import Schema from './VideoSlider.schema';
import VideoSlide from './VideoSlide';

export default function VideoSlider({ cms }) {
  const { heading, videoSlides } = cms;
  const carouselId = useId();
  const [activeVideoSlide, setActiveVideoSlide] = useState(null);
  const [globalMuteState, setGlobalMuteState] = useState(true);

  const sliderRef = useRef(null);

  const prefersReducedMotion = () => {
    // bail if user prefers reduced motion
    const checkReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );
    return checkReducedMotion?.matches === true;
  };

  // handle IntersectionObserver for mobile
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.innerWidth >= 575) return;
    if (prefersReducedMotion()) return;
    if (!videoSlides.length) return;

    function handleIntersection(entries, observer) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // setActiveVideoSlide(videoSlideId);
          setActiveVideoSlide(0);

          // only observe once
          observer.unobserve(entry.target);
        }
      });
    }

    const observer = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: '0px',
      threshold: 0.8, // Trigger the callback when 80% of the element is visible
    });

    observer.observe(sliderRef.current);
  }, []);

  const handleSlideChange = (swiper) => {
    const newIndex = swiper.realIndex ?? null;
    setActiveVideoSlide(newIndex);
  };

  return (
    <section className="bg-clearBlue py-20">
      <div className="space-y-10">
        <div className="mx-auto max-w-[55.75rem] space-y-6 text-center">
          <Headline variant="h3">{heading}</Headline>
        </div>

        {videoSlides && videoSlides.length !== 0 && (
          <div className="px-contained relative">
            <Swiper
              ref={sliderRef}
              loop
              grabCursor
              slidesPerView={1}
              spaceBetween={16}
              navigation={{
                nextEl: `[data-swiper-nav-id="carousel-${carouselId}-next"]`,
                prevEl: `[data-swiper-nav-id="carousel-${carouselId}-prev"]`,
                disabledClass: 'cursor-not-allowed opacity-20',
              }}
              pagination={{
                type: 'progressbar',
                el: `[data-swiper-nav-id="carousel-${carouselId}-pagination"]`,
              }}
              modules={[Navigation, Pagination]}
              breakpoints={{
                // Tablet - Portrait
                575: {
                  slidesPerView: 3.25,
                },
                // Tablet - Landscape
                768: {
                  slidesPerView: 3.5,
                },
                // Desktop
                1200: {
                  slidesPerView: 5,
                },
              }}
              onSlideChange={handleSlideChange}
              className="pb-8"
            >
              {videoSlides?.map((slide, index) => (
                <SwiperSlide key={index}>
                  <VideoSlide
                    activeVideoSlide={activeVideoSlide}
                    setActiveVideoSlide={setActiveVideoSlide}
                    globalMuteState={globalMuteState}
                    setGlobalMuteState={setGlobalMuteState}
                    videoSlide={slide}
                    slideIndex={index}
                  />
                </SwiperSlide>
              ))}
              <button
                type="button"
                title="Previous Slide"
                className="absolute left-2 top-1/4 z-10 flex !h-[28px] !w-[28px] translate-y-full items-center justify-center rounded-full bg-white after:hidden"
                data-swiper-nav-id={`carousel-${carouselId}-prev`}
              >
                <span className="sr-only">Previous Slide</span>
                <Svg
                  className="w-[0.8rem] text-black"
                  src="/svgs/arrow-left.svg#arrow-left"
                  title="Arrow Left"
                  viewBox="0 0 24 24"
                />
              </button>

              <button
                type="button"
                title="Next Slide"
                className="absolute left-auto right-2 top-1/4 z-10 flex !h-[28px] !w-[28px] translate-y-full items-center justify-center rounded-full bg-white after:hidden"
                data-swiper-nav-id={`carousel-${carouselId}-next`}
              >
                <span className="sr-only">Next Slide</span>
                <Svg
                  className="w-[0.8rem] text-black"
                  src="/svgs/arrow-right.svg#arrow-right"
                  title="Arrow Right"
                  viewBox="0 0 24 24"
                />
              </button>
              <div
                className="swiper-pagination !top-auto bottom-0 left-0 right-0 z-10 h-1 bg-white"
                style={{ '--swiper-pagination-color': 'var(--secondary)' }}
                data-swiper-nav-id={`carousel-${carouselId}-pagination`}
              />
            </Swiper>
          </div>
        )}
      </div>
    </section>
  );
}

VideoSlider.Schema = Schema;
